import React, { useRef, useState } from 'react';
import './videoAssetCard.css';

/** poster/thumb 误传为视频直链时不能用 <img>，须用 video 拉首帧。 */
const looksLikeVideoFileUrl = (u: string): boolean =>
  /\.(mp4|webm|mov|m4v|mkv|avi)(\?|#|$)/i.test((u || '').trim());

export interface VideoAssetCardAction {
  key: string;
  icon: React.ReactNode;
  title?: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

export interface VideoAssetCardProps {
  /** 视频播放地址 */
  videoUrl?: string | null;
  /** 封面图 URL */
  poster?: string | null;
  /** 卡片标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
  /** 额外信息 */
  meta?: string;

  /* ---------- 交互 ---------- */
  /** 居中操作按钮 */
  centerAction?: {
    label: string;
    onClick: (e: React.MouseEvent) => void;
  };
  centerActions?: Array<{
    label: string;
    onClick: (e: React.MouseEvent) => void;
  }>;
  /** 右上角删除按钮 */
  deleteAction?: {
    onClick: (e: React.MouseEvent) => void;
    busy?: boolean;
    disabled?: boolean;
  };
  /** 底部操作按钮组 */
  footerActions?: VideoAssetCardAction[];

  /* ---------- 布局 ---------- */
  /** 卡片变体 */
  variant?: 'card' | 'compact' | 'mini';
  /** 整个卡片的 onClick */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * 列表中只显示静态封面（与任务卡片一致），不在悬停时播放视频。
   * 实际播放由外层「预览」等操作打开。
   */
  staticThumbnail?: boolean;
  /** 额外 className */
  className?: string;
  /** 额外 style */
  style?: React.CSSProperties;

  /** 子元素 */
  children?: React.ReactNode;
}

/**
 * VideoAssetCard - 专门用于展示视频的素材卡片组件
 * 样式与 AssetCard 保持一致，增加视频预览功能
 */
export const VideoAssetCard: React.FC<VideoAssetCardProps> = ({
  videoUrl,
  poster,
  title,
  subtitle,
  meta,
  centerAction,
  centerActions,
  deleteAction,
  footerActions,
  variant = 'card',
  onClick,
  staticThumbnail = false,
  className,
  style,
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    if (staticThumbnail) return;
    setIsHovered(true);
    if (videoRef.current && videoUrl) {
      videoRef.current.play().catch(() => {
        // 自动播放失败通常是因为浏览器限制，静音通常可以解决
      });
    }
  };

  const handleMouseLeave = () => {
    if (staticThumbnail) return;
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const resolvedCenterActions =
    centerActions && centerActions.length > 0
      ? centerActions
      : centerAction
        ? [centerAction]
        : [];

  const posterTrim = (poster || '').trim();
  const videoTrim = (videoUrl || '').trim();
  const thumbSrc = posterTrim || '';
  const posterIsImage = posterTrim && !looksLikeVideoFileUrl(posterTrim);
  const staticVideoSrc =
    videoTrim || (staticThumbnail && posterTrim && looksLikeVideoFileUrl(posterTrim) ? posterTrim : '');

  const rootCls = [
    'vac-root',
    `vac-root--${variant}`,
    staticThumbnail ? 'vac-root--static-thumb' : '',
    onClick ? 'vac-root--clickable' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootCls}
      style={style}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* ====== 预览区 ====== */}
      <div className="vac-preview">
        {staticThumbnail && thumbSrc && posterIsImage ? (
          <img className="vac-media" src={thumbSrc} alt={title || ''} referrerPolicy="no-referrer" loading="lazy" />
        ) : staticThumbnail && staticVideoSrc ? (
          <video
            className="vac-media"
            src={staticVideoSrc}
            poster={posterIsImage ? posterTrim : undefined}
            muted
            playsInline
            preload="metadata"
            tabIndex={-1}
            aria-hidden
          />
        ) : videoTrim ? (
          <video
            ref={videoRef}
            className="vac-media"
            src={videoTrim}
            poster={posterIsImage ? posterTrim : undefined}
            muted
            loop
            playsInline
          />
        ) : posterTrim && posterIsImage ? (
          <img className="vac-media" src={posterTrim} alt={title || ''} referrerPolicy="no-referrer" />
        ) : (
          <div className="vac-empty-preview">无内容</div>
        )}

        {/* 视频标签/图标 */}
        <div className="vac-video-tag">
          <span className="vac-icon">🎬</span>
          {variant !== 'mini' && <span className="vac-label">视频</span>}
        </div>

        {/* 静态列表：始终显示播放标识；悬停预览模式：仅在未悬停时显示 */}
        {(staticThumbnail ? thumbSrc || staticVideoSrc || videoTrim : !isHovered && videoTrim) ? (
          <div className="vac-play-hint">
            <span className="vac-play-icon">▶</span>
          </div>
        ) : null}

        {/* 居中操作按钮 */}
        {resolvedCenterActions.length > 0 && (
          <div className="vac-center-overlay">
            {resolvedCenterActions.map((action, idx) => (
              <button
                key={`${action.label}-${idx}`}
                type="button"
                className="vac-center-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* 删除按钮 */}
        {deleteAction && (
          <button
            type="button"
            className="vac-delete-btn"
            disabled={deleteAction.disabled || deleteAction.busy}
            onClick={(e) => {
              e.stopPropagation();
              deleteAction.onClick(e);
            }}
          >
            {deleteAction.busy ? '⏳' : '✕'}
          </button>
        )}
      </div>

      {/* ====== 信息区 ====== */}
      {(title || subtitle || meta || footerActions || children) && (
        <div className="vac-info">
          {title && <div className="vac-title">{title}</div>}
          {subtitle && <div className="vac-subtitle">{subtitle}</div>}
          {meta && <div className="vac-meta">{meta}</div>}

          {children}

          {/* 底部操作按钮 */}
          {footerActions && footerActions.length > 0 && (
            <div className="vac-footer-actions">
              {footerActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="vac-footer-btn"
                  title={action.title}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(e);
                  }}
                >
                  {action.icon}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoAssetCard;
